import { useTranslation } from 'react-i18next'

import { Field } from '@/components/ui/Field'
import { Textarea } from '@/components/ui/Field'
import { FormSheet } from '@/components/ui/FormSheet'
import { Select } from '@/components/ui/Select'
import { CaseStatusPill } from '@/components/ui/StatusPill'
import { useFormDraft } from '@/components/ui/useFormDraft'
import type { StatusSendPayload } from '@/features/cases/api/useCase'
import type { CaseDetail } from '@/types/domain'

/* Telling the client where their shipment is (FR-41).
 *
 * A sheet rather than a one-click send, because this is the rare action in the
 * app that leaves the system permanently: an email cannot be unsent, and the
 * address it goes to is a fact worth reading before committing rather than
 * after. The confirmation is the recipient line, not a second "are you sure" —
 * a dialog that only asks for a second click teaches people to click twice.
 *
 * The status itself is NOT editable here. This sheet reports the case's current
 * status; changing it is the transition endpoint's job, and a control that
 * silently did both would let "tell the client" become "close the case".
 */

/** The languages a client is written to in. The team's own interface language
 *  is a separate setting — the person sending and the person reading rarely
 *  share one, which is why this defaults to Russian rather than to the UI. */
const LANGUAGES = ['ru', 'en'] as const

export function CaseStatusSendSheet({
  open,
  onClose,
  record,
  onSubmit,
  pending,
  submitError,
}: {
  open: boolean
  onClose: () => void
  record: CaseDetail
  onSubmit: (values: StatusSendPayload) => void
  pending?: boolean
  /** What the SERVER refused, if it did. */
  submitError?: string
}) {
  const { t } = useTranslation()

  const [draft, setDraft] = useFormDraft<StatusSendPayload>(
    open ? record.id : null,
    () => ({ note: '', language: 'ru' }),
  )

  /* A client with no address on file cannot be told anything. This is a
     standing limitation rather than a failure — nothing went wrong, the
     directory is simply incomplete — so it renders as a neutral notice and
     disables the send, instead of a red error over a form that could never
     have worked. */
  const recipient = record.clientContactEmail?.trim()
  const missingContact = !recipient

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={t('cases.statusSend.title')}
      submitLabel={t('cases.statusSend.submit')}
      onSubmit={() => onSubmit(draft)}
      pending={pending}
      disabled={missingContact}
      error={submitError}
      notice={missingContact ? t('cases.statusSend.noContact') : undefined}
    >
      <div className="flex flex-col gap-4">
        {/* The two facts the sender is committing to, stated before the fields
            they can actually change: who receives this, and what it will say.
            Read-only on purpose — the recipient comes from the client record so
            a send cannot be redirected from here, and the status is reported
            rather than set. */}
        <div className="border-line bg-surface-sunken flex flex-col gap-3 rounded-lg border px-4 py-3">
          <div className="flex flex-col gap-1.5">
            <span className="asa-eyebrow">{t('cases.statusSend.recipient')}</span>
            {missingContact ? (
              <span className="text-text-muted text-[13px]">—</span>
            ) : (
              <span className="text-text-heading text-[13px] leading-snug">
                {record.clientContactName ? (
                  <>
                    {record.clientContactName}{' '}
                    <span className="text-text-muted">&lt;{recipient}&gt;</span>
                  </>
                ) : (
                  recipient
                )}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="asa-eyebrow">{t('cases.statusSend.status')}</span>
            <span className="flex">
              <CaseStatusPill status={record.status} />
            </span>
          </div>
        </div>

        <Field
          label={t('cases.statusSend.language')}
          hint={t('cases.statusSend.languageHint')}
        >
          {(props) => (
            <Select
              {...props}
              value={draft.language}
              onChange={(language) => setDraft((d) => ({ ...d, language }))}
              /* The full names, not the "EN"/"RU" chips the topbar switcher
                 uses: this picks the language a CLIENT is written in, and a
                 two-letter code beside a client's address invites the glance
                 that mistakes it for the sender's own interface setting. */
              options={LANGUAGES.map((value) => ({
                value,
                label: t(value === 'ru' ? 'common.language.russian' : 'common.language.english'),
              }))}
              disabled={missingContact}
            />
          )}
        </Field>

        <Field label={t('cases.statusSend.note')} hint={t('cases.statusSend.noteHint')}>
          {(props) => (
            <Textarea
              {...props}
              value={draft.note}
              onChange={(event) =>
                setDraft((d) => ({ ...d, note: event.target.value }))
              }
              placeholder={t('cases.statusSend.notePlaceholder')}
              disabled={missingContact}
              rows={4}
            />
          )}
        </Field>
      </div>
    </FormSheet>
  )
}
